import { useEffect, useState } from 'react';
import Button from '../../../components/Button';
import type { OcdDetailsQuestionTemplate } from '../services/settingsTypes';
import { ocdDetailsQuestionsService } from '../services/ocdDetailsQuestionsService';
import { OcdDetailsQuestionEditModal } from './OcdDetailsQuestionEditModal';
import { OcdDetailsQuestionRow } from './OcdDetailsQuestionRow';

export const OcdDetailsQuestionsManager = () => {
    const [templates, setTemplates] = useState<OcdDetailsQuestionTemplate[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingTemplate, setEditingTemplate] = useState<OcdDetailsQuestionTemplate | null>(null);
    const [originalOcdProfileKey, setOriginalOcdProfileKey] = useState<string>('');
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

    useEffect(() => {
        fetchTemplates();
    }, []);

    const fetchTemplates = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const fetchedTemplates = await ocdDetailsQuestionsService.getQuestionTemplates();
            // Sort by order
            const sortedTemplates = [...fetchedTemplates].sort((a, b) => a.order - b.order);
            setTemplates(sortedTemplates);
        } catch (err) {
            console.error('Failed to fetch question templates:', err);
            setError('Failed to load question templates');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (template: OcdDetailsQuestionTemplate) => {
        setEditingTemplate(template);
        setOriginalOcdProfileKey(template.ocdProfileKey);
    };

    const handleAddNew = () => {
        setIsAddingNew(true);
    };

    const handleDelete = async (template: OcdDetailsQuestionTemplate) => {
        if (!confirm(`Are you sure you want to delete the question template for "${template.ocdProfileKey}"?`)) {
            return;
        }

        try {
            await ocdDetailsQuestionsService.deleteQuestionTemplate(template.ocdProfileKey);
            setTemplates(templates.filter(t => t.ocdProfileKey !== template.ocdProfileKey));
        } catch (err) {
            console.error('Failed to delete template:', err);
            setError('Failed to delete question template');
        }
    };

    const handleSave = async (template: OcdDetailsQuestionTemplate, isNew: boolean) => {
        try {
            if (isNew) {
                await ocdDetailsQuestionsService.addQuestionTemplate(template);
            } else {
                await ocdDetailsQuestionsService.updateQuestionTemplate(originalOcdProfileKey, template);
            }
            await fetchTemplates();
            setEditingTemplate(null);
            setOriginalOcdProfileKey('');
            setIsAddingNew(false);
        } catch (err) {
            console.error('Failed to save template:', err);
            throw err;
        }
    };

    const handleDragStart = (index: number) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === index) {
            return;
        }

        const newTemplates = [...templates];
        const draggedTemplate = newTemplates[draggedIndex];
        newTemplates.splice(draggedIndex, 1);
        newTemplates.splice(index, 0, draggedTemplate);

        setTemplates(newTemplates);
        setDraggedIndex(index);
    };

    const handleDragEnd = async () => {
        if (draggedIndex === null) {
            return;
        }

        try {
            await ocdDetailsQuestionsService.reorderQuestionTemplates(templates);
        } catch (err) {
            console.error('Failed to reorder templates:', err);
            setError('Failed to save template order');
            await fetchTemplates(); // Reload to reset order
        } finally {
            setDraggedIndex(null);
        }
    };

    const handleInitializeTemplates = async () => {
        if (!confirm('This will initialize the question templates with default values from the app. Continue?')) {
            return;
        }

        const defaultTemplates: OcdDetailsQuestionTemplate[] = [
            {
                questionTemplate: 'What is the theme that is bothering you the most right now?',
                questionContext: 'onboarding_most_distressing_theme',
                subtitle: "Don't worry, if you don't know for sure, just choose which one is closest now.",
                ocdProfileKey: 'onboarding_most_distressing_theme',
                order: 0,
            },
            {
                questionTemplate: 'When scary {previous answer} thoughts show up, are they more about...',
                questionContext: 'onboarding_obsession_type',
                subtitle: 'Select the one that speaks to you the most',
                ocdProfileKey: 'onboarding_obsession_type',
                order: 1,
            },
            {
                questionTemplate: 'When thoughts about {previous answer} show up, do they sound more like…',
                questionContext: 'onboarding_obsession_fear',
                subtitle: 'Select the one that speaks to you the most',
                ocdProfileKey: 'onboarding_obsession_fear',
                order: 2,
            },
            {
                questionTemplate: 'What do you usually do to try to feel safer or to make sure nothing bad happens?',
                questionContext: 'onboarding_compulsion',
                subtitle: 'Select the one that speaks to you the most',
                ocdProfileKey: 'onboarding_compulsion',
                order: 3,
            },
            {
                questionTemplate: "OCD isn't just about the thought or what you do in response.",
                questionContext: 'onboarding_core_fear',
                subtitle: "It's the fear underneath that makes it so sticky. Which of these feels closest to you?",
                ocdProfileKey: 'onboarding_core_fear',
                order: 4,
            },
        ];

        try {
            await ocdDetailsQuestionsService.updateQuestionTemplates(defaultTemplates);
            await fetchTemplates();
        } catch (err) {
            console.error('Failed to initialize templates:', err);
            setError('Failed to initialize question templates');
        }
    };

    if (isLoading) {
        return <div className="text-gray-600">Loading question templates...</div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap justify-between items-center gap-2">
                <h2 className="text-lg font-bold text-gray-900">OCD Details Question Templates</h2>
                <div className="flex flex-wrap gap-2">
                    {templates.length === 0 && (
                        <Button variant="secondary" size="sm" onClick={handleInitializeTemplates}>
                            Initialize Default Templates
                        </Button>
                    )}
                    <Button variant="primary" size="sm" onClick={handleAddNew}>
                        Add Template
                    </Button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {templates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    No question templates configured. Click "Initialize Default Templates" to get started.
                </div>
            ) : (
                <div className="space-y-2">
                    {templates.map((template, index) => (
                        <OcdDetailsQuestionRow
                            key={template.ocdProfileKey}
                            template={template}
                            index={index}
                            isDragging={draggedIndex === index}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {(editingTemplate || isAddingNew) && (
                <OcdDetailsQuestionEditModal
                    template={editingTemplate}
                    isNew={isAddingNew}
                    onSave={handleSave}
                    onClose={() => {
                        setEditingTemplate(null);
                        setOriginalOcdProfileKey('');
                        setIsAddingNew(false);
                    }}
                />
            )}
        </div>
    );
};

